import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { IonLabel, IonSpinner } from "@ionic/react";
import { Select, Button, message, InputNumber, Progress, Radio, Tooltip, Badge, Slider, Switch, Segmented, Divider } from "antd";
import { DownloadOutlined, SettingOutlined, InfoCircleOutlined, ExpandOutlined, LockOutlined, UnlockOutlined, CheckCircleOutlined, ThunderboltOutlined, SyncOutlined } from "@ant-design/icons";
import { stringify } from "svgson";
import { parseEmbroideryFile } from "./stitchParser";
import injectEmbroideryAttributes from "../helpers/injectEmbroideryAttributes";
import { getEmbroideryServiceUrl } from "../../../config/embroideryServiceUrl";
import StitchPreviewCanvas, { centerPanForZoom, BASE_W, BASE_H, VIEWPORT_W, VIEWPORT_H } from "./StitchPreviewCanvas";

// Common embroidery hoop sizes (in mm)
const HOOP_SIZES = [
  { label: "4×4 inch (100×100mm)", width: 100, height: 100 },
  { label: "5×7 inch (130×180mm)", width: 130, height: 180 },
  { label: "6×10 inch (160×260mm)", width: 160, height: 260 },
  { label: "8×8 inch (200×200mm)", width: 200, height: 200 },
  { label: "Custom", width: null, height: null },
];

// Supported embroidery formats
const MACHINE_FORMATS = [
  { value: "dst", label: "DST - Tajima (Most Universal)", popular: true },
  { value: "pes", label: "PES - Brother", popular: true },
  { value: "jef", label: "JEF - Janome", popular: true },
  { value: "vp3", label: "VP3 - Pfaff" },
  { value: "exp", label: "EXP - Melco" },
  { value: "pec", label: "PEC - Brother" },
  { value: "xxx", label: "XXX - Singer" },
  { value: "u01", label: "U01 - Barudan" },
];

const OTHER_FORMATS = [
  { value: "csv", label: "CSV - Spreadsheet/Debug" },
  { value: "json", label: "JSON - Data Exchange" },
  { value: "gcode", label: "G-code - CNC" },
];

// Fabric presets - maps to optimal embroidery settings
const FABRIC_PRESETS = [
  { 
    value: "cotton", 
    label: "🧵 Cotton / Light Fabric", 
    description: "T-shirts, light linens",
    needle: "75/11",
    needleTip: "75/11 embroidery needle — standard for lightweight cotton and linen",
    settings: {
      row_spacing_mm: 0.2,
      fill_underlay: true,
      fill_underlay_row_spacing_mm: 3.0,
      pull_compensation_mm: 0.2,
    }
  },
  { 
    value: "poly_blend", 
    label: "👕 Poly Blend / Medium", 
    description: "Polos, athletic wear",
    needle: "75/11",
    needleTip: "75/11 or 80/12 embroidery needle — handles synthetic blends and stretchy knits",
    settings: {
      row_spacing_mm: 0.2,
      fill_underlay: true,
      fill_underlay_row_spacing_mm: 2.5,
      pull_compensation_mm: 0.25,
    }
  },
  { 
    value: "fleece", 
    label: "🧥 Fleece / Hoodie", 
    description: "Sweatshirts, heavy knits",
    needle: "80/12",
    needleTip: "80/12 embroidery needle — prevents skipped stitches on thick fleece and knits",
    settings: {
      row_spacing_mm: 0.2,
      fill_underlay: true,
      fill_underlay_row_spacing_mm: 2.0,
      pull_compensation_mm: 0.35,
    }
  },
  { 
    value: "denim", 
    label: "👖 Denim / Canvas", 
    description: "Jeans, bags, heavy fabrics",
    needle: "90/14",
    needleTip: "90/14 embroidery needle — sturdy enough to punch through denim and canvas cleanly",
    settings: {
      row_spacing_mm: 0.2,
      fill_underlay: true,
      fill_underlay_row_spacing_mm: 2.5,
      pull_compensation_mm: 0.15,
    }
  },
  { 
    value: "hat", 
    label: "🧢 Hat / Cap", 
    description: "Structured caps, visors",
    needle: "90/14",
    needleTip: "90/14 sharp embroidery needle — handles structured cap material and multiple layers",
    settings: {
      row_spacing_mm: 0.2,
      fill_underlay: true,
      fill_underlay_row_spacing_mm: 1.5,
      pull_compensation_mm: 0.3,
    }
  },
  { 
    value: "custom", 
    label: "⚙️ Custom Settings", 
    description: "Manual control",
    needle: null,
    needleTip: null,
    settings: null
  },
];

// Underlay types
const UNDERLAY_TYPES = [
  { value: "none", label: "None", description: "No underlay (faster, less stable)" },
  { value: "auto", label: "Auto", description: "Standard fill underlay" },
  { value: "zigzag", label: "Zigzag", description: "Better coverage, more thread" },
  { value: "contour", label: "Contour", description: "Edge definition, good for text" },
];

// Stitch types — values must match Ink/Stitch FillStitch `fill_method` names
const STITCH_TYPES = [
  { value: "auto_fill", label: "Auto fill", icon: "▰", description: "Automatically routed fill — default for solid shapes", inkstitchMethod: "auto_fill" },
  { value: "cross_stitch", label: "Cross stitch", icon: "✕", description: "Cross-hatch fill pattern (Ink/Stitch cross stitch)", inkstitchMethod: "cross_stitch" },
  { value: "meander", label: "Meander", icon: "⌇", description: "Meandering decorative fill path", inkstitchMethod: "meander_fill" },
];

// Stitch angle presets
const ANGLE_PRESETS = [
  { value: null, label: "Auto", icon: "🔄" },
  { value: 0, label: "Horizontal", icon: "↔️" },
  { value: 90, label: "Vertical", icon: "↕️" },
  { value: 45, label: "Diagonal ↗", icon: "↗️" },
  { value: -45, label: "Diagonal ↘", icon: "↘️" },
];

// StitchPreviewCanvas and its helpers live in StitchPreviewCanvas.jsx


const EmbroideryConverter = ({ svgJoined, exportName, layerStitchSettings, layerRails }) => {
  const [selectedFormat, setSelectedFormat] = useState("dst");
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [conversionResult, setConversionResult] = useState(null);
  const [realStitchData, setRealStitchData] = useState(null);
  const [isAutoConverting, setIsAutoConverting] = useState(false);
  const autoPreviewRanRef = useRef(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const previewPanRef = useRef(centerPanForZoom(1));
  const [previewPan, setPreviewPan] = useState(centerPanForZoom(1));
  const [error, setError] = useState(null);

  // Size controls
  const [selectedHoop, setSelectedHoop] = useState(0); // Index into HOOP_SIZES
  const [targetWidth, setTargetWidth] = useState(100);
  const [targetHeight, setTargetHeight] = useState(100);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [sizeUnit, setSizeUnit] = useState("mm"); // mm or inch

  // === NEW: Embroidery Optimization Controls ===
  const [selectedFabric, setSelectedFabric] = useState("cotton");
  const [stitchSettings, setStitchSettings] = useState({
    // Stitch type
    stitch_type: "auto_fill", // auto_fill | cross_stitch | meander (Ink/Stitch fill_method)
    // Fill stitch settings
    angle: null, // null = auto, or degrees
    row_spacing_mm: 0.2, // Density (lower = denser)
    // Underlay settings
    fill_underlay: true,
    underlay_type: "auto", // none, auto, zigzag, contour
    fill_underlay_row_spacing_mm: 3.0,
    fill_underlay_angle: null, // null = perpendicular to fill
    // Pull compensation
    pull_compensation_mm: 0.2,
    // Satin settings
    zigzag_spacing_mm: 0.4,
    // Optimization
    optimize_order: true,
  });
  const [wizardStep, setWizardStep] = useState(1);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const goToStep2 = useCallback(() => setWizardStep(2), []);

  // Migrate removed / invalid stitch_type values (one-time on mount)
  useEffect(() => {
    setStitchSettings((prev) => {
      const legacy = { cross_fill: "cross_stitch", satin: "auto_fill", running: "auto_fill" };
      const t = prev.stitch_type;
      if (legacy[t]) return { ...prev, stitch_type: legacy[t] };
      if (!STITCH_TYPES.some((x) => x.value === t)) return { ...prev, stitch_type: "auto_fill" };
      return prev;
    });
  }, []);

  // Calculate original SVG dimensions
  const originalDimensions = useMemo(() => {
    if (!svgJoined?.attributes?.viewBox) {
      return { width: 100, height: 100, aspectRatio: 1 };
    }
    const viewBox = svgJoined.attributes.viewBox.split(" ");
    const width = parseFloat(viewBox[2]) || 100;
    const height = parseFloat(viewBox[3]) || 100;
    return { width, height, aspectRatio: width / height };
  }, [svgJoined]);

  // Apply fabric preset
  const applyFabricPreset = (fabricValue) => {
    setSelectedFabric(fabricValue);
    const preset = FABRIC_PRESETS.find(f => f.value === fabricValue);
    if (preset?.settings) {
      setStitchSettings(prev => ({
        ...prev,
        ...preset.settings
      }));
    }
  };

  // Initialize target size based on original aspect ratio
  useEffect(() => {
    const hoop = HOOP_SIZES[selectedHoop];
    if (hoop.width && hoop.height) {
      // Fit within hoop while maintaining aspect ratio
      const hoopAspect = hoop.width / hoop.height;
      if (originalDimensions.aspectRatio > hoopAspect) {
        // Width-constrained
        setTargetWidth(hoop.width);
        setTargetHeight(Math.round(hoop.width / originalDimensions.aspectRatio));
      } else {
        // Height-constrained
        setTargetHeight(hoop.height);
        setTargetWidth(Math.round(hoop.height * originalDimensions.aspectRatio));
      }
    }
  }, [selectedHoop, originalDimensions]);

  // Handle width change with aspect ratio lock
  const handleWidthChange = (newWidth) => {
    setTargetWidth(newWidth);
    if (lockAspectRatio && newWidth) {
      setTargetHeight(Math.round(newWidth / originalDimensions.aspectRatio));
    }
  };

  // Handle height change with aspect ratio lock
  const handleHeightChange = (newHeight) => {
    setTargetHeight(newHeight);
    if (lockAspectRatio && newHeight) {
      setTargetWidth(Math.round(newHeight * originalDimensions.aspectRatio));
    }
  };

  // Convert between mm and inches for display
  const mmToInch = (mm) => (mm / 25.4).toFixed(2);
  const inchToMm = (inch) => Math.round(inch * 25.4);

  const [options, setOptions] = useState({
    collapse_len: 3.0,
    min_stitch_len: 0.1,
    disable_ties: false,
    max_stitch: null,
    max_jump: null,
    no_trims: false,
  });

  // Inject inkstitch attributes into SVG elements for embroidery optimization
  const injectInkstitchAttributes = (element, settings) => {
    if (!element) return element;

    // Initialize attributes if not present
    if (!element.attributes) {
      element.attributes = {};
    }

    // Check if this is an embroiderable element (path, rect, circle, etc.)
    const embroiderable = ["path", "rect", "circle", "ellipse", "polygon", "polyline", "line"];
    const isEmbroiderable = embroiderable.includes(element.name);

    // Only inject into embroiderable elements or groups
    if (isEmbroiderable) {
      // Stitch type (fill method)
      {
        const stitchTypeDef = STITCH_TYPES.find(t => t.value === settings.stitch_type) || STITCH_TYPES[0];
        element.attributes["inkstitch:fill_method"] = stitchTypeDef.inkstitchMethod;
      }

      // Fill angle
      if (settings.angle !== null && settings.angle !== undefined) {
        element.attributes["inkstitch:angle"] = String(settings.angle);
      }

      // Row spacing (density)
      if (settings.row_spacing_mm) {
        element.attributes["inkstitch:row_spacing_mm"] = String(settings.row_spacing_mm);
      }

      // Underlay settings
      element.attributes["inkstitch:fill_underlay"] = settings.fill_underlay ? "true" : "false";

      if (settings.fill_underlay) {
        if (settings.fill_underlay_row_spacing_mm) {
          element.attributes["inkstitch:fill_underlay_row_spacing_mm"] = String(settings.fill_underlay_row_spacing_mm);
        }
        if (settings.fill_underlay_angle !== null && settings.fill_underlay_angle !== undefined) {
          element.attributes["inkstitch:fill_underlay_angle"] = String(settings.fill_underlay_angle);
        }

        // Underlay type specific attributes
        if (settings.underlay_type === "zigzag") {
          element.attributes["inkstitch:zigzag_underlay"] = "true";
        } else if (settings.underlay_type === "contour") {
          element.attributes["inkstitch:contour_underlay"] = "true";
        }
      }

      // Pull compensation (important for edge quality)
      if (settings.pull_compensation_mm) {
        element.attributes["inkstitch:pull_compensation_mm"] = String(settings.pull_compensation_mm);
      }

      // Satin specific settings
      if (settings.zigzag_spacing_mm) {
        element.attributes["inkstitch:zigzag_spacing_mm"] = String(settings.zigzag_spacing_mm);
      }
    }

    // Recursively process children
    if (element.children && Array.isArray(element.children)) {
      element.children = element.children.map(child => injectInkstitchAttributes(child, settings));
    }

    return element;
  };

  // Scale SVG to target dimensions and inject embroidery settings
  const scaleSvgToSize = (svgJson, targetWidthMm, targetHeightMm) => {
    // Clone the SVG JSON to avoid mutating the original
    const scaledSvg = JSON.parse(JSON.stringify(svgJson));

    // Get original viewBox dimensions
    const viewBox = scaledSvg.attributes.viewBox?.split(" ") || ["0", "0", "100", "100"];
    const origWidth = parseFloat(viewBox[2]);
    const origHeight = parseFloat(viewBox[3]);

    // Calculate scale to convert viewBox units to mm
    // SVG uses 96 DPI by default, so 1 inch = 96 pixels = 25.4mm
    // Therefore 1 pixel ≈ 0.2646 mm
    const PIXELS_PER_MM = 96 / 25.4; // ~3.78 pixels per mm

    // Set width and height in mm
    scaledSvg.attributes.width = `${targetWidthMm}mm`;
    scaledSvg.attributes.height = `${targetHeightMm}mm`;

    // Add inkstitch namespace to SVG root
    scaledSvg.attributes["xmlns:inkstitch"] = "http://inkstitch.org/namespace";

    // Scale the viewBox to match the target size ratio
    const scaleX = targetWidthMm * PIXELS_PER_MM / origWidth;
    const scaleY = targetHeightMm * PIXELS_PER_MM / origHeight;

    // Use per-layer settings when available from embroidery mode, otherwise fall back to global settings
    const hasPerLayerSettings = layerStitchSettings && Object.keys(layerStitchSettings).length > 0;
    const injectedSvg = hasPerLayerSettings
      ? injectEmbroideryAttributes(scaledSvg, layerStitchSettings, layerRails)
      : injectInkstitchAttributes(scaledSvg, stitchSettings);

    return injectedSvg;
  };

  const handleConvert = async () => {
    setIsConverting(true);
    setConversionProgress(10);
    setError(null);
    setConversionResult(null);
    setRealStitchData(null);

    try {
      const scaledSvg = scaleSvgToSize(svgJoined, targetWidth, targetHeight);
      const svgContent = stringify(scaledSvg);
      setConversionProgress(20);

      const response = await fetch(`${getEmbroideryServiceUrl()}/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          svgContent,
          format: selectedFormat,
          options: {
            collapse_len: options.collapse_len,
            min_stitch_len: options.min_stitch_len,
            disable_ties: options.disable_ties,
            max_stitch: options.max_stitch,
            max_jump: options.max_jump,
            no_trims: options.no_trims,
          },
        }),
      });

      setConversionProgress(70);

      if (!response.ok) {
        throw new Error(`Server error (${response.status})`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Conversion failed");
      }

      setConversionProgress(90);
      setConversionResult(result);

      if (result.data) {
        try {
          const parsed = parseEmbroideryFile(result.data, selectedFormat);
          if (parsed) setRealStitchData(parsed);
        } catch (parseErr) {
          console.warn('[EmbroideryConverter] Stitch parse failed:', parseErr);
        }
      }

      setConversionProgress(100);

      message.success(`Converted to ${selectedFormat.toUpperCase()} successfully!`);
    } catch (err) {
      console.error("Embroidery conversion error:", err);
      const msg = err.message || "Failed to convert. Please try again.";
      setError(msg);
      message.error(msg);
    } finally {
      setIsConverting(false);
    }
  };

  // Auto-convert to DST when user reaches Step 3 (Preview & Export)
  useEffect(() => {
    if (wizardStep !== 3 || !svgJoined || autoPreviewRanRef.current || realStitchData || isConverting) return;
    autoPreviewRanRef.current = true;

    const autoConvert = async () => {
      setIsAutoConverting(true);
      try {
        const scaledSvg = scaleSvgToSize(svgJoined, targetWidth, targetHeight);
        const svgContent = stringify(scaledSvg);

        const response = await fetch(`${getEmbroideryServiceUrl()}/convert`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            svgContent,
            format: "dst",
            options: {
              collapse_len: options.collapse_len,
              min_stitch_len: options.min_stitch_len,
              disable_ties: options.disable_ties,
              max_stitch: options.max_stitch,
              max_jump: options.max_jump,
              no_trims: options.no_trims,
            },
          }),
        });

        const result = await response.json();
        if (result.success && result.data) {
          const parsed = parseEmbroideryFile(result.data, "dst");
          if (parsed) setRealStitchData(parsed);
          setConversionResult(result);
          setConversionProgress(100);
        }
      } catch (err) {
        console.warn('[EmbroideryConverter] Auto-preview conversion failed:', err);
      } finally {
        setIsAutoConverting(false);
      }
    };

    const timer = setTimeout(autoConvert, 300);
    return () => { clearTimeout(timer); };
  }, [wizardStep, svgJoined]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    if (!conversionResult) return;

    // Decode base64 and create blob
    const binaryString = atob(conversionResult.data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "application/octet-stream" });

    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportName || "embroidery"}.${selectedFormat}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    message.success(`Downloaded ${exportName}.${selectedFormat}`);
  };

  const formatBytes = (meters) => {
    if (!meters) return "0 m";
    return `${meters.toFixed(2)} m`;
  };

  // Step indicator labels
  const STEPS = [
    { num: 1, label: "Fabric" },
    { num: 2, label: "Size" },
    { num: 3, label: "Preview & Export" },
  ];

  return (
    <div style={{ padding: "10px 0" }}>

      {/* ── Step Indicator ── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0",
        marginBottom: "12px",
      }}>
        {STEPS.map((step, idx) => (
          <div key={step.num} style={{ display: "flex", alignItems: "center" }}>
            <div
              onClick={() => {
                if (step.num < wizardStep) setWizardStep(step.num);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: step.num <= wizardStep ? "pointer" : "default",
                opacity: step.num <= wizardStep ? 1 : 0.4,
                transition: "all 0.2s ease",
              }}
            >
              <div style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 700,
                background: step.num < wizardStep ? "#52c41a" : step.num === wizardStep ? "#F453C4" : "#e0e0e0",
                color: step.num <= wizardStep ? "#fff" : "#999",
                transition: "all 0.2s ease",
              }}>
                {step.num < wizardStep ? "✓" : step.num}
              </div>
              <span style={{
                fontSize: "11px",
                fontWeight: step.num === wizardStep ? 700 : 500,
                color: step.num === wizardStep ? "#333" : step.num < wizardStep ? "#52c41a" : "#999",
                fontFamily: "Clash Grotesk, sans-serif",
                letterSpacing: "0.2px",
              }}>
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div style={{
                width: "28px",
                height: "2px",
                background: step.num < wizardStep ? "#52c41a" : "#e0e0e0",
                margin: "0 8px",
                borderRadius: "1px",
                transition: "background 0.2s ease",
              }} />
            )}
          </div>
        ))}
      </div>

      {/* ════════════════════ STEP 1: FABRIC ════════════════════ */}
      {wizardStep === 1 && (
        <>
          <div style={{
            marginBottom: "10px",
            textAlign: "center",
          }}>
            <h3 style={{
              margin: "0 0 2px",
              fontSize: "15px",
              fontWeight: 700,
              fontFamily: "Clash Grotesk, sans-serif",
              color: "#333",
            }}>
              Select Your Fabric
            </h3>
            <p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
              Optimizes stitch density, underlay, and pull compensation for your material
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
            marginBottom: "12px",
          }}>
            {FABRIC_PRESETS.filter(p => p.value !== "custom").map(preset => {
              const isSelected = selectedFabric === preset.value;
              return (
                <div
                  key={preset.value}
                  onClick={() => applyFabricPreset(preset.value)}
                  style={{
                    padding: "10px",
                    borderRadius: "8px",
                    border: isSelected ? "2px solid #F453C4" : "2px solid #e8e8e8",
                    background: isSelected ? "linear-gradient(135deg, #fdf4ff 0%, #fff 100%)" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: isSelected ? "0 2px 12px rgba(244,83,196,0.15)" : "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "2px",
                  }}>
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>{preset.label.split(" ")[0]}</span>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#333" }}>
                      {preset.label.split(" ").slice(1).join(" ")}
                    </span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                    {preset.description}
                  </div>
                  {preset.needle && (
                    <span style={{
                      fontSize: "10px",
                      color: isSelected ? "#F453C4" : "#aaa",
                      fontWeight: 600,
                    }}>
                      🪡 {preset.needle}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Advanced settings toggle */}
          <div
            onClick={() => setShowAdvancedSettings(s => !s)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              fontSize: "11px",
              color: showAdvancedSettings ? "#F453C4" : "#999",
              cursor: "pointer",
              padding: "2px 0 8px",
            }}
          >
            <SettingOutlined />
            {showAdvancedSettings ? "Hide advanced" : "Advanced settings"}
          </div>

          {showAdvancedSettings && (
            <div style={{
              marginBottom: "10px",
              padding: "12px",
              background: "linear-gradient(135deg, #fdf4ff 0%, #f8f9fa 100%)",
              borderRadius: "8px",
              border: "1px solid #f0e0f5",
              maxHeight: "280px",
              overflowY: "auto",
            }}>
              {/* Stitch Type */}
              <div style={{ marginBottom: "10px" }}>
                <IonLabel style={{ fontSize: "11px", color: "#666", display: "block", marginBottom: "4px", fontWeight: 600 }}>
                  Stitch Type
                </IonLabel>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {STITCH_TYPES.map(type => (
                    <Tooltip key={type.value} title={type.description}>
                      <Button
                        type={stitchSettings.stitch_type === type.value ? "primary" : "default"}
                        size="small"
                        onClick={() => { setStitchSettings(prev => ({ ...prev, stitch_type: type.value })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }}
                        style={{
                          background: stitchSettings.stitch_type === type.value ? "#F453C4" : undefined,
                          borderColor: stitchSettings.stitch_type === type.value ? "#F453C4" : undefined,
                          fontSize: "11px",
                          height: "26px",
                          padding: "0 8px",
                        }}
                      >
                        {type.icon} {type.label}
                      </Button>
                    </Tooltip>
                  ))}
                </div>
              </div>

              {/* Stitch Angle */}
              <div style={{ marginBottom: "10px" }}>
                <IonLabel style={{ fontSize: "11px", color: "#666", display: "block", marginBottom: "4px", fontWeight: 600 }}>
                  Stitch Angle
                </IonLabel>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginBottom: "4px" }}>
                  {ANGLE_PRESETS.map(preset => (
                    <Button
                      key={preset.value ?? "auto"}
                      type={stitchSettings.angle === preset.value ? "primary" : "default"}
                      size="small"
                      onClick={() => { setStitchSettings(prev => ({ ...prev, angle: preset.value })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }}
                      style={{
                        background: stitchSettings.angle === preset.value ? "#F453C4" : undefined,
                        borderColor: stitchSettings.angle === preset.value ? "#F453C4" : undefined,
                        fontSize: "11px",
                        height: "26px",
                        padding: "0 8px",
                      }}
                    >
                      {preset.icon} {preset.label}
                    </Button>
                  ))}
                </div>
                {stitchSettings.angle !== null && (
                  <Slider value={stitchSettings.angle} onChange={(val) => { setStitchSettings(prev => ({ ...prev, angle: val })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }} min={-90} max={90} marks={{ [-90]: "-90°", 0: "0°", 90: "90°" }} tooltip={{ formatter: (val) => `${val}°` }} />
                )}
              </div>

              {/* Stitch Density */}
              <div style={{ marginBottom: "10px" }}>
                <IonLabel style={{ fontSize: "11px", color: "#666", display: "block", marginBottom: "2px", fontWeight: 600 }}>
                  Stitch Density <span style={{ fontWeight: 400, color: "#aaa" }}>({stitchSettings.row_spacing_mm}mm)</span>
                </IonLabel>
                <Slider value={stitchSettings.row_spacing_mm} onChange={(val) => { setStitchSettings(prev => ({ ...prev, row_spacing_mm: val })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }} min={0.15} max={0.6} step={0.05} marks={{ 0.15: "Dense", 0.35: "Normal", 0.6: "Light" }} tooltip={{ formatter: (val) => `${val}mm` }} />
              </div>

              <Divider style={{ margin: "8px 0" }} />

              {/* Underlay */}
              <div style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                  <IonLabel style={{ fontSize: "11px", color: "#666", fontWeight: 600 }}>Underlay</IonLabel>
                  <Switch checked={stitchSettings.fill_underlay} onChange={(checked) => { setStitchSettings(prev => ({ ...prev, fill_underlay: checked })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }} size="small" />
                </div>
                {stitchSettings.fill_underlay && (
                  <div style={{ padding: "8px", background: "rgba(255,255,255,0.7)", borderRadius: "6px" }}>
                    <Segmented value={stitchSettings.underlay_type} onChange={(val) => { setStitchSettings(prev => ({ ...prev, underlay_type: val })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }} options={UNDERLAY_TYPES.map(t => ({ value: t.value, label: <Tooltip title={t.description}><span style={{ fontSize: "11px" }}>{t.label}</span></Tooltip> }))} style={{ width: "100%", marginBottom: "6px" }} />
                    <IonLabel style={{ fontSize: "10px", color: "#888" }}>Density: {stitchSettings.fill_underlay_row_spacing_mm}mm</IonLabel>
                    <Slider value={stitchSettings.fill_underlay_row_spacing_mm} onChange={(val) => { setStitchSettings(prev => ({ ...prev, fill_underlay_row_spacing_mm: val })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }} min={1} max={5} step={0.5} marks={{ 1: "Dense", 3: "Normal", 5: "Light" }} />
                  </div>
                )}
              </div>

              {/* Pull Compensation */}
              <div style={{ marginBottom: "6px" }}>
                <IonLabel style={{ fontSize: "11px", color: "#666", display: "block", marginBottom: "2px", fontWeight: 600 }}>
                  Pull Compensation <span style={{ fontWeight: 400, color: "#aaa" }}>({stitchSettings.pull_compensation_mm}mm)</span>
                </IonLabel>
                <Slider value={stitchSettings.pull_compensation_mm} onChange={(val) => { setStitchSettings(prev => ({ ...prev, pull_compensation_mm: val })); if (selectedFabric !== "custom") setSelectedFabric("custom"); }} min={0} max={0.5} step={0.05} marks={{ 0: "None", 0.25: "Med", 0.5: "Max" }} tooltip={{ formatter: (val) => `${val}mm` }} />
              </div>

              <Divider style={{ margin: "8px 0" }} />

              {/* Advanced conversion options */}
              <IonLabel style={{ fontSize: "11px", color: "#666", display: "block", marginBottom: "6px", fontWeight: 600 }}>
                Conversion Options
              </IonLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginBottom: "8px" }}>
                <div>
                  <IonLabel style={{ fontSize: "10px", color: "#888" }}>Collapse Len (mm)</IonLabel>
                  <InputNumber value={options.collapse_len} onChange={(value) => setOptions({ ...options, collapse_len: value })} min={0.1} max={20} step={0.5} style={{ width: "100%" }} size="small" />
                </div>
                <div>
                  <IonLabel style={{ fontSize: "10px", color: "#888" }}>Min Stitch (mm)</IonLabel>
                  <InputNumber value={options.min_stitch_len} onChange={(value) => setOptions({ ...options, min_stitch_len: value })} min={0.01} max={5} step={0.1} style={{ width: "100%" }} size="small" />
                </div>
                <div>
                  <IonLabel style={{ fontSize: "10px", color: "#888" }}>Max Stitch (mm)</IonLabel>
                  <InputNumber value={options.max_stitch} onChange={(value) => setOptions({ ...options, max_stitch: value })} min={1} max={50} step={1} placeholder="Auto" style={{ width: "100%" }} size="small" />
                </div>
                <div>
                  <IonLabel style={{ fontSize: "10px", color: "#888" }}>Max Jump (mm)</IonLabel>
                  <InputNumber value={options.max_jump} onChange={(value) => setOptions({ ...options, max_jump: value })} min={1} max={200} step={5} placeholder="Auto" style={{ width: "100%" }} size="small" />
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Switch checked={options.disable_ties} onChange={(checked) => setOptions({ ...options, disable_ties: checked })} size="small" />
                  <span style={{ fontSize: "11px", color: "#666" }}>Disable Ties</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Switch checked={options.no_trims} onChange={(checked) => setOptions({ ...options, no_trims: checked })} size="small" />
                  <span style={{ fontSize: "11px", color: "#666" }}>No Trims</span>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "8px" }}>

            <Button
              type="primary"
              block
              onClick={goToStep2}
              style={{
                background: "#F453C4",
                borderColor: "#F453C4",
                height: "38px",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Next — Set Size →
            </Button>
          </div>
        </>
      )}

      {/* ════════════════════ STEP 2: SIZE ════════════════════ */}
      {wizardStep === 2 && (
        <>
          <div style={{
            marginBottom: "10px",
            textAlign: "center",
          }}>
            <h3 style={{
              margin: "0 0 2px",
              fontSize: "15px",
              fontWeight: 700,
              fontFamily: "Clash Grotesk, sans-serif",
              color: "#333",
            }}>
              Set Embroidery Size
            </h3>
            <p style={{ margin: 0, fontSize: "11px", color: "#888" }}>
              Choose a hoop size or enter custom dimensions
            </p>
          </div>

      {/* Size Selection */}
      <div style={{ 
        marginBottom: "10px", 
        padding: "12px", 
        background: "#f8f9fa", 
        borderRadius: "8px",
        border: "1px solid #e9ecef"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <IonLabel
            style={{
              color: "#333",
              fontFamily: "Clash Grotesk",
              fontSize: "14px",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <ExpandOutlined /> Embroidery Size
          </IonLabel>
          <Radio.Group 
            value={sizeUnit} 
            onChange={(e) => setSizeUnit(e.target.value)}
            size="small"
            buttonStyle="solid"
          >
            <Radio.Button value="mm">mm</Radio.Button>
            <Radio.Button value="inch">inch</Radio.Button>
          </Radio.Group>
        </div>

        {/* Hoop Size Presets */}
        <div style={{ marginBottom: "12px" }}>
          <IonLabel style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "6px" }}>
            Hoop Size Preset
          </IonLabel>
          <Select
            value={selectedHoop}
            onChange={(index) => {
              setSelectedHoop(index);
              const hoop = HOOP_SIZES[index];
              if (hoop.width && hoop.height) {
                // Fit design within hoop
                const hoopAspect = hoop.width / hoop.height;
                if (originalDimensions.aspectRatio > hoopAspect) {
                  handleWidthChange(hoop.width);
                } else {
                  handleHeightChange(hoop.height);
                }
              }
            }}
            style={{ width: "100%" }}
            disabled={isConverting}
          >
            {HOOP_SIZES.map((hoop, index) => (
              <Select.Option key={index} value={index}>
                {hoop.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Custom Size Inputs */}
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <IonLabel style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "4px" }}>
              Width {sizeUnit === "inch" ? "(inch)" : "(mm)"}
            </IonLabel>
            <InputNumber
              value={sizeUnit === "mm" ? targetWidth : parseFloat(mmToInch(targetWidth))}
              onChange={(val) => handleWidthChange(sizeUnit === "mm" ? val : inchToMm(val))}
              min={10}
              max={500}
              step={sizeUnit === "mm" ? 5 : 0.5}
              style={{ width: "100%" }}
              disabled={isConverting}
              addonAfter={sizeUnit}
            />
          </div>

          <Button
            type="text"
            icon={lockAspectRatio ? <LockOutlined /> : <UnlockOutlined />}
            onClick={() => setLockAspectRatio(!lockAspectRatio)}
            style={{ 
              color: lockAspectRatio ? "#F453C4" : "#999",
              marginBottom: "4px"
            }}
            title={lockAspectRatio ? "Unlock aspect ratio" : "Lock aspect ratio"}
          />

          <div style={{ flex: 1 }}>
            <IonLabel style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "4px" }}>
              Height {sizeUnit === "inch" ? "(inch)" : "(mm)"}
            </IonLabel>
            <InputNumber
              value={sizeUnit === "mm" ? targetHeight : parseFloat(mmToInch(targetHeight))}
              onChange={(val) => handleHeightChange(sizeUnit === "mm" ? val : inchToMm(val))}
              min={10}
              max={500}
              step={sizeUnit === "mm" ? 5 : 0.5}
              style={{ width: "100%" }}
              disabled={isConverting}
              addonAfter={sizeUnit}
            />
          </div>
        </div>

        {/* Size Preview */}
        <div style={{ 
          marginTop: "12px", 
          padding: "8px 12px", 
          background: "#fff", 
          borderRadius: "4px",
          fontSize: "12px",
          color: "#666",
          display: "flex",
          justifyContent: "space-between"
        }}>
          <span>Output: <strong>{targetWidth}×{targetHeight} mm</strong></span>
          <span>({mmToInch(targetWidth)}×{mmToInch(targetHeight)} inch)</span>
        </div>
      </div>

          {/* Step 2 navigation */}
          <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
            <Button
              onClick={() => setWizardStep(1)}
              style={{
                height: "38px",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                fontSize: "13px",
                borderRadius: "8px",
                flex: "0 0 auto",
              }}
            >
              ← Back
            </Button>

            <Button
              type="primary"
              block
              onClick={() => setWizardStep(3)}
              style={{
                background: "#F453C4",
                borderColor: "#F453C4",
                height: "38px",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Next — Preview & Export →
            </Button>
          </div>
        </>
      )}

      {/* ════════════════════ STEP 3: PREVIEW & EXPORT ════════════════════ */}
      {wizardStep === 3 && (
        <>
          {/* Stitch Preview */}
          <div style={{
            marginBottom: "10px",
            padding: "10px 12px",
            background: "linear-gradient(135deg, #1a1a2e 0%, #14213d 50%, #1a1a2e 100%)",
            borderRadius: "12px",
            border: "1px solid rgba(244,83,196,0.2)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.18), 0 1px 3px rgba(0,0,0,0.1)",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "6px",
            }}>
              <IonLabel style={{
                color: "#fff",
                fontFamily: "Clash Grotesk",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                letterSpacing: "0.2px",
              }}>
                <ThunderboltOutlined style={{ color: "#F453C4" }} /> Stitch Preview
              </IonLabel>
              <Badge
                count={realStitchData ? "STITCHED" : isAutoConverting ? "CONVERTING" : "LIVE"}
                style={{
                  backgroundColor: realStitchData ? "#52c41a" : isAutoConverting ? "#faad14" : "#F453C4",
                  fontSize: "9px",
                  boxShadow: realStitchData ? "0 0 8px rgba(82,196,26,0.4)" : isAutoConverting ? "0 0 8px rgba(250,173,20,0.4)" : "0 0 8px rgba(244,83,196,0.3)",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                }}
              />
            </div>

            <div style={{ position: "relative" }}>
              <StitchPreviewCanvas
                svgJoined={svgJoined}
                stitchSettings={stitchSettings}
                targetWidth={targetWidth}
                targetHeight={targetHeight}
                realStitchData={realStitchData}
                zoom={previewZoom}
                setZoom={setPreviewZoom}
                pan={previewPan}
                setPan={setPreviewPan}
                panRef={previewPanRef}
              />

              {(isAutoConverting || isConverting) && (
                <div style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(20,18,30,0.75)",
                  backdropFilter: "blur(4px)",
                  borderRadius: "10px",
                  zIndex: 10,
                  gap: "12px",
                }}>
                  <div style={{
                    width: "36px",
                    height: "36px",
                    border: "3px solid rgba(255,255,255,0.1)",
                    borderTopColor: "#F453C4",
                    borderRadius: "50%",
                    animation: "stitchSpin 0.8s linear infinite",
                  }} />
                  <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "12px", fontWeight: 600, fontFamily: "Clash Grotesk, sans-serif" }}>
                    Generating stitches…
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "10px" }}>
                    Converting with Ink/Stitch engine
                  </span>
                  <style>{`@keyframes stitchSpin { to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
            </div>

            {/* Zoom controls */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "6px",
              padding: "4px 10px",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(0,0,0,0.1)",
              borderRadius: "0 0 8px 8px",
              marginLeft: "-12px",
              marginRight: "-12px",
              marginBottom: "-10px",
            }}>
              <div style={{ width: "52px" }} />
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Button size="small" onClick={() => { setPreviewZoom(z => { const newZ = Math.max(0.5, z / 1.5); const cx = VIEWPORT_W / 2, cy = VIEWPORT_H / 2; const s = newZ / z; const p = { x: cx - s * (cx - previewPanRef.current.x), y: cy - s * (cy - previewPanRef.current.y) }; previewPanRef.current = p; setPreviewPan(p); return newZ; }); }}
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)", borderRadius: "6px", width: "28px", height: "28px", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                >−</Button>
                <span style={{ color: previewZoom !== 1 ? "#F453C4" : "rgba(255,255,255,0.45)", fontSize: "11px", minWidth: "42px", textAlign: "center", fontWeight: 600, fontFamily: "'SF Mono', monospace" }}>
                  {Math.round(previewZoom * 100)}%
                </span>
                <Button size="small" onClick={() => { setPreviewZoom(z => { const newZ = Math.min(10, z * 1.5); const cx = VIEWPORT_W / 2, cy = VIEWPORT_H / 2; const s = newZ / z; const p = { x: cx - s * (cx - previewPanRef.current.x), y: cy - s * (cy - previewPanRef.current.y) }; previewPanRef.current = p; setPreviewPan(p); return newZ; }); }}
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)", borderRadius: "6px", width: "28px", height: "28px", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                >+</Button>
              </div>
              <div style={{ width: "52px", display: "flex", justifyContent: "flex-end" }}>
                {previewZoom !== 1 && (
                  <Button size="small" onClick={() => { setPreviewZoom(1); const rp = centerPanForZoom(1); previewPanRef.current = rp; setPreviewPan(rp); }}
                    style={{ background: "rgba(244,83,196,0.12)", border: "1px solid rgba(244,83,196,0.5)", color: "#F453C4", borderRadius: "6px", padding: "0 10px", height: "28px", fontSize: "10px", fontWeight: 600 }}
                  >Reset</Button>
                )}
              </div>
            </div>

            <div style={{ marginTop: "4px", fontSize: "10px", textAlign: "center", letterSpacing: "0.3px" }}>
              {realStitchData ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", flexWrap: "wrap" }}>
                  <span style={{ color: "#52c41a", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "3px" }}>
                    <CheckCircleOutlined style={{ fontSize: "10px" }} /> Real stitch paths
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.35)" }}>·</span>
                  <span style={{ color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>{realStitchData.stitches.length.toLocaleString()} stitches</span>
                  <span style={{ color: "rgba(255,255,255,0.35)" }}>·</span>
                  <span style={{ color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>{realStitchData.colors.length} color{realStitchData.colors.length !== 1 ? 's' : ''}</span>
                  {realStitchData.colors.length > 0 && (
                    <>
                      <span style={{ color: "rgba(255,255,255,0.35)" }}>·</span>
                      <span style={{ display: "inline-flex", gap: "2px" }}>
                        {realStitchData.colors.slice(0, 8).map((c, i) => (
                          <span key={i} style={{ width: "8px", height: "8px", borderRadius: "50%", background: c, display: "inline-block", border: "1px solid rgba(255,255,255,0.2)", boxShadow: "0 0 2px rgba(0,0,0,0.3)" }} />
                        ))}
                      </span>
                    </>
                  )}
                </div>
              ) : isAutoConverting ? (
                <span style={{ color: "rgba(244,83,196,0.7)", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <IonSpinner name="dots" style={{ width: 12, height: 12 }} />
                  Generating stitch preview…
                </span>
              ) : (
                <span style={{ color: "rgba(255,255,255,0.3)" }}>Convert to see real stitch paths</span>
              )}
            </div>
          </div>

      {/* Error */}
      {error && (
        <div style={{
          background: "#fff2f0",
          border: "1px solid #ffccc7",
          borderRadius: "6px",
          padding: "8px 12px",
          marginBottom: "8px",
          color: "#cf1322",
          fontSize: "12px",
        }}>
          <InfoCircleOutlined style={{ marginRight: 6 }} />
          {error}
        </div>
      )}

      {/* Progress */}
      {isConverting && (
        <div style={{ marginBottom: "8px" }}>
          <Progress 
            percent={conversionProgress} 
            status="active"
            strokeColor="#F453C4"
            size="small"
          />
        </div>
      )}

      {/* Result Statistics — compact */}
      {conversionResult && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "8px 12px",
          background: "#f6ffed",
          border: "1px solid #b7eb8f",
          borderRadius: "6px",
          marginBottom: "8px",
          fontSize: "12px",
          flexWrap: "wrap",
        }}>
          <span style={{ color: "#52c41a", fontWeight: 600 }}>✓ {(conversionResult.metadata?.num_stitches || 0).toLocaleString()} stitches</span>
          <span style={{ color: "#d9d9d9" }}>·</span>
          <span style={{ color: "#52c41a", fontWeight: 600 }}>{conversionResult.metadata?.num_colors || 1} color{(conversionResult.metadata?.num_colors || 1) !== 1 ? "s" : ""}</span>
          <span style={{ color: "#d9d9d9" }}>·</span>
          <span style={{ color: "#52c41a", fontWeight: 600 }}>{formatBytes(conversionResult.metadata?.estimated_thread_meters)}</span>
          {conversionResult.metadata?.dimensions_mm && (
            <>
              <span style={{ color: "#d9d9d9" }}>·</span>
              <span style={{ color: "#888", fontWeight: 500 }}>{conversionResult.metadata.dimensions_mm.width?.toFixed(1)} × {conversionResult.metadata.dimensions_mm.height?.toFixed(1)} mm</span>
            </>
          )}
        </div>
      )}

      {/* Format + Convert/Download — single row */}
      <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
        <Select
          value={selectedFormat}
          onChange={setSelectedFormat}
          style={{ flex: 1 }}
          disabled={isConverting}
        >
          <Select.OptGroup label="Machine Formats">
            {MACHINE_FORMATS.map((format) => (
              <Select.Option key={format.value} value={format.value}>
                {format.label}
                {format.popular && (
                  <span style={{ marginLeft: 8, background: "#F453C4", color: "white", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>Popular</span>
                )}
              </Select.Option>
            ))}
          </Select.OptGroup>
          <Select.OptGroup label="Other Formats">
            {OTHER_FORMATS.map((format) => (
              <Select.Option key={format.value} value={format.value}>
                {format.label}
              </Select.Option>
            ))}
          </Select.OptGroup>
        </Select>
        {!conversionResult ? (
          <Button
            type="primary"
            onClick={handleConvert}
            loading={isConverting}
            disabled={isConverting}
            style={{
              background: "#F453C4",
              borderColor: "#F453C4",
              fontWeight: 600,
              fontFamily: "Clash Grotesk",
              fontSize: "14px",
              padding: "0 20px",
              whiteSpace: "nowrap",
            }}
          >
            Convert
          </Button>
        ) : (
          <div style={{ display: "flex", gap: "6px" }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              style={{
                background: "#52c41a",
                borderColor: "#52c41a",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                padding: "0 16px",
                whiteSpace: "nowrap",
              }}
            >
              Download
            </Button>
            <Button
              onClick={() => { setConversionResult(null); setConversionProgress(0); }}
              style={{ fontWeight: 500, padding: "0 12px", whiteSpace: "nowrap" }}
            >
              Redo
            </Button>
          </div>
        )}
      </div>

      {/* Back link */}
      <div style={{ textAlign: "center", marginTop: "6px" }}>
        <Button
          type="text"
          onClick={() => setWizardStep(2)}
          style={{ color: "#999", fontSize: "11px", fontWeight: 500, padding: "2px 0" }}
        >
          ← Back to settings
        </Button>
      </div>
        </>
      )}
    </div>
  );
};

export default EmbroideryConverter;
